<template>
  <div class="catalog-product-modal">
    <div class="catalog-product-main-wrapper">
      <div
        class="error-text"
        v-if="
          hasInvalidPage && Array.isArray(getRows) && getRows.length && images
        "
      >
        <img src="@/assets/images/icons/alert-circle.svg" />
        Tablodaki eksik veya yanlış girdiğiniz bilgileri düzenleyin. Varsa,
        tablonun diğer sayfalarını da kontrol edin.
      </div>
      <SingleProductTableBulkOperations
        v-show="getRowsCount && isBulkOperationsActive"
        ref="bulkOperationsRef"
        :selectedRowsLength="getSelectedRowsLength"
        @applySelected="applySelected"
        @bulk-image-upload="openBulkImageModal"
        pid="bulk"
        :selectedCurrency="getSelectedCurrency"
        :categoryId="category"
      />
      <SingleProductTable
        v-show="getRowsCount"
        ref="singleProductTableRef"
        :variants="tableRows"
        :attributes="attributes"
        :columns="columnList"
        :selectedCurrency="getSelectedCurrency"
      >
      </SingleProductTable>
      <AddImageModal
        ref="addImageModalRef"
        @images-uploaded="handleImagesUploaded"
        @modalClosed="handleModalClosed"
        :initial-images="modalInitialImages"
        :row-ids="modalRowIds"
        :allRows="getAllRows"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { v4 as uuidv4 } from "uuid";
import { Base64 } from "js-base64";
import useColumnService from "../services/getCatalogColumns";
import SingleProductTableBulkOperations from "./SingleProductTableBulkOperations.vue";
import AddImageModal from "./AddImageModal.vue";
import SingleProductTable from "./SingleProductTable";

const props = defineProps({
  attributes: { type: Array, default: [] },
  category: { type: [Number, null], required: true },
  selectedCurrency: { type: Object, required: true, default: {} },
  variantRefs: { type: Object, required: true, default: {} },
});

const store = useStore();
let rowIndex = 1;
const addImageModalRef = ref(null);
const tableRows = ref([]);
const combinationHistory = ref([]);
const bulkOperationsRef = ref();
const singleProductTableRef = ref();
const columnList = ref([]);
const isBulkOperationsActive = ref(false);
const bulkModalOpen = ref(false);
const validateRef = ref(null);

const getCategoryId = computed(() => props.category);
const getSelectedCurrency = computed(() => props.selectedCurrency);
const getSelectedRowsLength = computed(
  () => Object.keys(singleProductTableRef.value?.getSelectedRows() ?? []).length
);
const getRowsCount = computed(() => tableRows.value?.length);
const deletedRowId = computed(
  () => store.getters["products/getSingleProductDeleteRowId"]
);
const images = computed(() => store.getters["common/getHasImageError"]);
const imageUploadRowId = computed(
  () => store.getters["products/getSingleProductImageUploadRowId"]
);
const selectedRowIds = computed(
  () =>
    singleProductTableRef.value?.getSelectedRowIds?.() ??
    Object.keys(singleProductTableRef.value?.getSelectedRows() ?? [])
);
const hasInvalidPage = computed(() =>
  singleProductTableRef.value?.getInvalidPageNumbers()?.length === 0
    ? false
    : true
);
const modalRowIds = computed(() => {
  if (!bulkModalOpen.value) {
    return imageUploadRowId.value ? [imageUploadRowId.value] : [];
  }
  return selectedRowIds.value;
});
const modalInitialImages = computed(() =>
  bulkModalOpen.value
    ? []
    : tableRows.value.find((r) => r.id === imageUploadRowId.value)?.images || []
);
const getAllRows = computed(() =>
  singleProductTableRef.value?.table?.options?.meta.getRawData()
);
const getRows = computed(() =>
  singleProductTableRef.value?.table?.options?.meta.getData()
);

watch(imageUploadRowId, async (id) => {
  if (id) {
    bulkModalOpen.value = false;
    await nextTick();
    addImageModalRef.value?.openAddImageModal();
  }
});

watch(getCategoryId, (id) => {
  const { columns, validate } = useColumnService(id, props.selectedCurrency);
  validateRef.value = validate;
  columnList.value = columns;
});

watch(getSelectedCurrency, (currency) => {
  const { columns } = useColumnService(props.category, currency);
  columnList.value = columns;
});

watch(deletedRowId, (id) => {
  if (id) {
    removeRow(id);
    store.commit("products/SET_SINGLE_PRODUCT_DELETE_ROW_ID", null);
  }
});

watch(
  () => tableRows.value,
  (newRows, oldRows) => {
    if (newRows && oldRows) {
      const addedVariants = newRows?.filter(
        (obj2) => !oldRows?.some((obj1) => obj1.id === obj2.id)
      );
      const removedVariants = oldRows?.filter(
        (obj1) => !newRows?.some((obj2) => obj2.id === obj1.id)
      );
      const updatedImagesVariants = newRows?.filter((obj1) =>
        oldRows?.some(
          (obj2) =>
            obj2.id === obj1.id && obj1.imagesRowSpan !== obj2.imagesRowSpan
        )
      );
      const updatedImages = newRows?.filter((obj1) =>
        oldRows?.some(
          (obj2) =>
            obj2.id === obj1.id &&
            JSON.stringify(obj1.images) !== JSON.stringify(obj2.images)
        )
      );

      const findRemovedAttributes = (arr1, arr2, key) => {
        const arr2Map = new Map(arr2.map((item) => [item[key], item]));
        const removed = arr1.filter((item) => !arr2Map.has(item[key]));
        let notFoundRemovingIds = [];
        if (removed.length) {
          const removingIds = removed.map((r) =>
            r.attributes.map((ra) => ra.id)
          );
          const allIds = tableRows.value.map((r) =>
            r.attributes.map((ra) => ra.id)
          );
          const flatRemovingIds = removingIds.flat();
          const flatListingIds = allIds.flat();
          const listingSet = new Set(flatListingIds);
          notFoundRemovingIds = flatRemovingIds.filter(
            (id) => !listingSet.has(id)
          );
        }
        return notFoundRemovingIds;
      };

      const removedIds = findRemovedAttributes(
        removedVariants,
        tableRows.value,
        "attributes"
      );
      removedIds.map((r) =>
        props.variantRefs.map((v) => v.removeSelectedItem(r))
      );

      addedVariants.map((added) =>
        singleProductTableRef.value?.table?.options?.meta.addRow(added)
      );
      removedVariants.map((removed) =>
        singleProductTableRef.value?.table?.options?.meta.removeRow(removed.id)
      );
      updatedImagesVariants.map((updated) =>
        singleProductTableRef.value?.table?.options?.meta.updateRow(
          updated.id,
          "imagesRowSpan",
          updated.imagesRowSpan
        )
      );
      updatedImages.map((updated) =>
        singleProductTableRef.value?.table?.options?.meta.updateRow(
          updated.id,
          "images",
          updated.images
        )
      );
      singleProductTableRef.value?.table?.options?.meta.order();
    }
  }
);

const addRow = (variants, isSingle = false) => {
  const row = getRowTemplate(variants, isSingle);
  const isAlreadyExist = tableRows.value.some((r) => r.hash === row.hash);
  if (!isAlreadyExist) tableRows.value = [row, ...tableRows.value];

  const firstVariant = variants.find((v) => v.isSlicer) || variants[0];
  const prefix =
    firstVariant?.value || row.order.split("-").slice(0, -1).join("-");
  singleProductTableRef.value?.boostGroup?.(prefix);
};

const addBlankRow = () => {
  isBulkOperationsActive.value = false;
  const row = getRowTemplate([], false);
  tableRows.value = [row];
};

const removeRow = (id) =>
  (tableRows.value = [...tableRows.value.filter((item) => item.id !== id)]);
const updateImage = (id) =>
  (tableRows.value = [...tableRows.value.filter((item) => item.id !== id)]);
const resetTable = () => {
  isBulkOperationsActive.value = false;
  tableRows.value = [];
};

const updateCombinations = (combinations, isSingle) => {
  isBulkOperationsActive.value = true;
  const getSignature = (combo) =>
    combo
      .map((item) => item.id)
      .sort((a, b) => a - b)
      .join("-");
  const diffCombinations = (oldList, newList) => {
    const oldMap = new Map(oldList.map((c) => [getSignature(c), c]));
    const newMap = new Map(newList.map((c) => [getSignature(c), c]));
    const added = [],
      removed = [];
    for (const [sig, combo] of newMap)
      if (!oldMap.has(sig)) {
        delete combo.rowSpan;
        added.push(combo);
      }
    for (const [sig, combo] of oldMap)
      if (!newMap.has(sig)) {
        delete combo.rowSpan;
        removed.push(combo);
      }
    return { added, removed };
  };

  const { added, removed } = diffCombinations(
    combinationHistory.value,
    combinations
  );
  combinationHistory.value = combinations;
  added.map((comb) => addRow(comb, isSingle));
  removed.map((comb) => removeCombinationRows(comb, isSingle));
};

const resolveBulkTargetIds = () => {
  const ids = selectedRowIds.value;
  const visibleCount = (getRows.value || []).length;
  const totalCount = getRowsCount.value || 0;

  if (ids.length && ids.length === visibleCount && totalCount > visibleCount) {
    return (getAllRows.value || []).map((r) => r.id);
  }
  return ids;
};

const applySelected = () => {
  if (!singleProductTableRef.value) return;

  const ids = selectedRowIds.value;
  if (!ids.length) return;

  const ops = bulkOperationsRef.value?.bulkOperationsData;
  const updates = {};
  ["gtin", "listPrice", "salePrice", "stock", "duration", "vatRate"].forEach(
    (f) => {
      const v = ops?.[f];
      if (v !== undefined && v !== null && v !== "" && v !== 0) updates[f] = v;
    }
  );
  if (!Object.keys(updates).length) return;

  singleProductTableRef.value.applyBulkToIds?.(ids, updates);

  tableRows.value = tableRows.value.map((r) =>
    ids.includes(r.id) ? { ...r, ...updates } : r
  );
  singleProductTableRef.value.table.resetRowSelection();
};

const openBulkImageModal = async () => {
  bulkModalOpen.value = true;
  store.commit("products/SET_SINGLE_PRODUCT_IMAGE_UPLOAD_ROW_ID", null);
  await nextTick();
  addImageModalRef.value?.openAddImageModal();
};

const handleModalClosed = () => {
  store.commit("products/SET_SINGLE_PRODUCT_IMAGE_UPLOAD_ROW_ID", null);
  bulkModalOpen.value = false;
};

const handleImagesUploaded = ({ rowIds, images }) => {
  tableRows.value = tableRows.value.map((r) =>
    rowIds.includes(r.id) ? { ...r, images, imagesRowSpan: images.length } : r
  );
  handleModalClosed();
};

const isSlicer = (el) =>
  props.attributes.find((a) => a.attributeName === el.name)?.isSlicer;

const getRowTemplate = (variants, isSingle) => {
  const createEmptyValidation = () => ({
    isDirty: false,
    error: false,
    message: null,
    valid: true,
    loading: false,
  });
  variants.map((el) => (el.isSlicer = isSlicer(el)));
  const firstVariant = variants.find((el) => el.isSlicer) || variants[0];
  const row = {
    id: uuidv4(),
    images: [],
    imagesRowSpan: 1,
    attributes: variants,
    hashPlain: variants.length ? JSON.stringify(variants) : "",
    order: firstVariant
      ? firstVariant.value + "-" + rowIndex.toString().padStart(2, "0")
      : rowIndex.toString().padStart(2, "0"),
    hash: variants.length
      ? Base64.encode(
          variants
            .map((item) => item.id)
            .sort((a, b) => a - b)
            .join("-")
        )
      : "",
    gtin: null,
    stockCode: null,
    listPrice: null,
    salePrice: null,
    stock: null,
    duration: null,
    vatRate: null,
    isSingle,
    validations: [
      "stockCode",
      "listPrice",
      "gtin",
      "salePrice",
      "stock",
      "duration",
      "vatRate",
      "images",
    ].reduce((acc, key) => {
      acc[key] = createEmptyValidation();
      return acc;
    }, {}),
  };
  rowIndex++;
  return row;
};

const removeCombinationRows = (comb, isSingle) => {
  const cleanedComb = comb.map(({ rowSpan, ...rest }) => rest);
  tableRows.value = tableRows.value.filter(
    (item) =>
      item.isSingle === isSingle &&
      item.hash !==
        Base64.encode(
          cleanedComb
            .map((item) => item.id)
            .sort((a, b) => a - b)
            .join("-")
        )
  );
};

defineExpose({
  addRow,
  addBlankRow,
  removeRow,
  updateImage,
  resetTable,
  getRows,
  getAllRows,
  updateCombinations,
  validateRef,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
.catalog-product-main-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;

  .error-text {
    padding: 16px;
    display: flex;
    flex-direction: row;
    gap: 8px;
    font-size: 13px;
    line-height: 18px;
    color: $colorN80;
    background: $colorE05;
    border-radius: 6px;
    align-items: center;
    letter-spacing: -0.05px;

    img {
      width: 22px;
      height: 22px;
    }
  }
}
</style>
